import { TabButton } from '@/app/_common/split-view/draggable-tab'
import { DraggableTabs } from '@/app/_common/split-view/draggable-tabs'
import { cn } from '@/lib/utils'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDndContext,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import { Resizable } from 're-resizable'
import React from 'react'
import { useMeasure } from 'react-use'
import { TabsPanelId, useSplitView } from './split-view-context'

const MIN_PANEL_WIDTH = 400

type SplitViewProps = Readonly<{ enableAnimation?: boolean }>
function SplitView({ enableAnimation = true }: SplitViewProps) {
  const resizableRef = React.useRef<Resizable | null>(null)
  const [setContainerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>()
  const [isResizing, setIsResizing] = React.useState(false)
  const [hasInitialized, setHasInitialized] = React.useState(false)

  const { enableSplitView, moveTab, panelWidth, setPanelWidth } = useSplitView()
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  // Mark as initialized after first render
  React.useEffect(() => {
    setTimeout(() => setHasInitialized(true), 250)
  }, [])

  // Set width to half container when split view is enabled and no panel width is set
  React.useEffect(() => {
    if (enableSplitView && containerWidth > 0 && !panelWidth) {
      const halfWidth = containerWidth / 2
      setPanelWidth(halfWidth)
    }
  }, [enableSplitView, containerWidth, panelWidth, setPanelWidth])

  // Optionally, reset panelWidth to null when split view is disabled
  React.useEffect(() => {
    if (!enableSplitView && panelWidth !== null) {
      setPanelWidth(null)
    }
  }, [enableSplitView, panelWidth, setPanelWidth])

  // Determine actual width to use
  const computedWidth = React.useMemo(() => {
    if (!enableSplitView) {
      // When split view is disabled, always use full container width
      // Only use MIN_PANEL_WIDTH as fallback if containerWidth is not available
      const width = containerWidth > 0 ? containerWidth : MIN_PANEL_WIDTH
      return width
    }
    if (panelWidth) return panelWidth
    const width = containerWidth > 0 ? containerWidth / 2 : MIN_PANEL_WIDTH
    return width
  }, [enableSplitView, panelWidth, containerWidth])

  const isAnimationEnabled = !isResizing && enableAnimation && hasInitialized

  return (
    <div
      ref={(el) => el && setContainerRef(el)}
      className='flex h-full w-full gap-[9px] overflow-hidden'
    >
      <DndContext
        id='split-panel-draggable-tabs'
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={({ active, over }) => {
          if (over) {
            moveTab(String(active.id), String(over.id))
          }
        }}
        accessibility={{
          announcements: {
            onDragStart: ({ active }) => `Picked up ${active.id}`,
            onDragOver: ({ over }) => `Moved over ${over?.id}`,
            onDragCancel: ({ active }) => `Cancelled dragging ${active.id}`,
            onDragEnd: ({ active, over }) => `Dropped ${active.id} onto ${over?.id}`,
          },
        }}
      >
        <Resizable
          handleStyles={{ right: { width: 9, right: -9 } }}
          handleComponent={{
            right: (
              <div className='group/drag flex h-full w-[9px] cursor-ew-resize items-center justify-center bg-grey-100 transition-colors duration-200'>
                <div
                  className={cn(
                    'h-full w-px bg-neutral-150/75 group-hover/drag:bg-mango-300',
                    isResizing && 'bg-mango-300',
                  )}
                />
              </div>
            ),
          }}
          ref={resizableRef}
          size={{ height: '100%', width: computedWidth }}
          enable={{ right: enableSplitView }}
          maxWidth={enableSplitView ? containerWidth - MIN_PANEL_WIDTH : containerWidth}
          minWidth={enableSplitView ? MIN_PANEL_WIDTH : containerWidth}
          onResizeStart={() => setIsResizing(true)}
          onResizeStop={(_, __, ref) => {
            setIsResizing(false)
            if (!enableSplitView) return
            const newWidth = ref.offsetWidth
            if (newWidth <= containerWidth) {
              setPanelWidth(newWidth)
            }
          }}
          className={cn(
            'flex',
            isAnimationEnabled && 'transition-all delay-75 duration-200 ease-in-out',
          )}
        >
          <div className='h-full grow overflow-x-hidden'>
            <DraggableTabs panelId={TabsPanelId.Left} />
          </div>
        </Resizable>
        <div
          style={{ minWidth: enableSplitView ? MIN_PANEL_WIDTH : 0 }}
          className={cn(
            'h-full grow overflow-hidden',
            isAnimationEnabled && 'transition-[min-width] delay-75 duration-200 ease-in-out',
          )}
        >
          {enableSplitView && <DraggableTabs panelId={TabsPanelId.Right} />}
        </div>
        <DraggingTabOverlay />
      </DndContext>
    </div>
  )
}

function DraggingTabOverlay() {
  const { getTabPositionMetadata } = useSplitView()
  const { active } = useDndContext()
  const draggingTab = React.useMemo(() => {
    if (!active?.id) return null
    return getTabPositionMetadata({ tabId: String(active.id) })?.tab ?? null
  }, [active?.id, getTabPositionMetadata])

  return (
    <DragOverlay modifiers={[restrictToWindowEdges]}>
      {draggingTab && (
        <TabButton variant='dragging' tabType={draggingTab.type} title={draggingTab.title} />
      )}
    </DragOverlay>
  )
}

export { SplitView }
